import React, { useRef, useState, useCallback } from 'react'
import './HRLDetector.css'
import AnimatedCounter from '../../components/animated-counter';
import SubscribeForm from '../SubscribeFormFolder/SubscribeForm';

import {
    LazyLoadImage,
    LazyLoadComponent,
} from 'react-lazy-load-image-component';

// Assets
// Background video
const BannerBg = require('./videos/hrldetector-page-video.mp4');
const BannerBg_LazyLoadImg = require('./img/detector-lazyload.png');

// Images
import actionImage1 from './img/DetectorBlackTracing 1.png'
import detecBg2 from './img/detector-bg2.png'
import detecBg3 from './img/detector-bg3.png'
import detecBg4 from './img/detector-bg4.png'

// Map functionality and Design imports
import {
    MapContainer,
    TileLayer,
    GeoJSON
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/dist/styles.min.css";
import L, { LeafletEvent, GeoJSON as LeafletGeoJSON } from "leaflet";
import 'leaflet-geosearch/dist/geosearch.css';

// States Data
import statesData from './util/us-states'

const HRLDetector = () => {

    // Section for the video and its lazyLoad functionality
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);

    const geojsonRef = useRef<L.GeoJSON | null>(null); // Create a reference for the GeoJSON layer
    const [hoveredFeature, setHoveredFeature] = useState<{ name: string; density: number } | null>(null);
    const [showMap, setShowMap] = useState(false);
    const mapRef = useRef<HTMLDivElement | null>(null);
    const stratRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLHeadingElement>(null);

    const detectorStats = {
        area: {
            name: 'Detection Time, sec',
            value: 40,
        },
        hrls: {
            name: 'Stroke Detection Efficiency',
            value: 100,
        },
        fires: {
            name: 'Detection Accuracy, ft',
            value: 100,
        },
    };

    const NetStats = {
        area: {
            name: 'Continents with active High-Risk-Lightning™ coverage',
            value: 3,
        },
        fires: {
            name: 'Utility faults accessed and analyzed',
            value: 120,
        },
        hrls: {
            name: 'Lightning-ignited wildfires located in 2024',
            value: 164,
        },
    };

    function splitLabel(text:string) {
    // Split by both space and hyphen, but preserve the original separators
    const parts = text.split(/(?<=[ -])/); // Keep space/hyphen with the previous word
    const mid = Math.ceil(parts.length / 2);
    const firstLine = parts.slice(0, mid).join('');
    const secondLine = parts.slice(mid).join('');
    return `${firstLine}<br/>${secondLine}`;
}

    // Map Functions

    function getColor(d: number) {
        return d >= 1 ? '#28aae1' : '#bbb';
    }

    function style(feature: any) {
        return {
            fillColor: getColor(feature.properties.density),
            weight: 2,
            opacity: 1,
            color: 'white',
            dashArray: '3',
            fillOpacity: 0.5
        };
    }

    function highlightFeature(e: LeafletEvent) {
        const layer = e.target as L.Path; // Cast to Leaflet Path for setStyle

        layer.setStyle({
            weight: 5,
            color: "#666",
            dashArray: "",
            fillOpacity: 0.7,
        });

        layer.bringToFront();
    }

    // Reset highlight on mouseout
    function resetHighlight(e: LeafletEvent) {
        if (geojsonRef.current) {
            geojsonRef.current.resetStyle(e.target);
        }
    }

    // Zoom to feature on click with some padding
    // function zoomToFeature(e: LeafletEvent) {
    //     const map = e.target._map;
    //     if (map) {
    //         map.fitBounds(e.target.getBounds(), {
    //             padding: [20, 20],
    //             maxZoom: map.getZoom() + 2, // Adjust this to limit zoom-in
    //         });
    //     }
    // }

    // Function to handle each GeoJSON feature
    function onEachFeature(feature: GeoJSON.Feature, layer: any) {
        const density = feature?.properties?.density;

        if (density >= 1) {
            layer.on({
                mouseover: (e: LeafletEvent) => {
                    const layer = e.target as L.Path;
                    highlightFeature(e);

                    if (feature.properties) {
                        setHoveredFeature({
                            name: feature.properties.name,
                            density: feature.properties.density,
                        });
                    }
                },
                mouseout: (e: LeafletEvent) => {
                    resetHighlight(e);
                    setHoveredFeature(null); // Clear the hovered data when the mouse leaves
                },
                // click: zoomToFeature,
            });
        } else {
            // Optional: remove pointer events if density ≤ 1
            layer.getElement()?.classList.add('no-hover');
        }
    }

    const computedStyle = useCallback((feature: any) => style(feature), []);


    return (
        <div className="detector-container">

            {/* Cover Section */}
            <div className="detector-hero-section">
                {!isVideoLoaded && (
                    <LazyLoadComponent
                        placeholder={<img src={BannerBg_LazyLoadImg} alt="" />}
                    >
                        <LazyLoadImage
                            src={BannerBg_LazyLoadImg}
                            alt="="
                            className="hero-image"
                        />
                    </LazyLoadComponent>
                )}

                <video
                    className={`hero-image ${isVideoLoaded ? '' : 'hidden'}`}
                    src={BannerBg}
                    autoPlay
                    loop
                    muted
                    playsInline
                    onLoadedData={() => setIsVideoLoaded(true)}
                />

                <div className="bright-overlay"></div> {/* ← Overlay */}

                <div className="detector-info-wrapper">
                    <h1><strong>High-Risk-Lightning™ <br /> Detector</strong></h1>
                </div>
            </div>

            {/* Detec Section */}
            <div className='overall-detec-section'>

                <div className="detec-section">

                    <h1 className='overall-detec-title'><strong>AI-Driven Detection<br />Patented Lightning Response</strong></h1>

                    <div className='detec-info-wrapper'>
                        <h1 className='the-detector-h1'>Powered by Patented High-Risk-Lightning™ Technology</h1>

                        <h2 className='the-detector-h2'>FNN™ uses a patented <span className='glow-blue'>High-Risk-Lightning™ sensor technology </span>
                            to accurately pinpoint fault locations in <span className='glow-blue'> 40 seconds</span> with a
                            <span className='glow-blue'> 100-foot precision</span>.</h2>

                        <button className='the-detector-button'
                            onClick={() => {
                                if (triggerRef.current) {
                                    const yOffset = -45; // scroll 80px above the element
                                    const y = triggerRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                    window.scrollTo({ top: y, behavior: 'smooth' });
                                }
                            }}
                        >Learn More</button>
                    </div>
                </div>

                <img className="detec-image" alt="detec background" src={actionImage1} />

            </div>

            {/* Precision Section */}
            <div className="detector-hero-section">
                <img className="hero-image1" alt="hero background" src={detecBg2} />
                <div className="dark-overlay"></div> {/* ← Overlay */}

                <div className={`precision-info-wrapper align-right`}>
                    <div className="precision-infos">
                        <h1><strong>Enhanced Detection Efficiency Through Precision Lightning Data</strong></h1>
                        <h2>
                            The High-Risk-Lightning™ detector’s AI enabled design supports precise current duration
                            and charge transfer measurements, allowing for highly accurate <span className='glow-blue'>
                                3D lightning mapping </span> and efficient detection. Especially, during Atmospheric Catastrophic
                            Events™.
                        </h2>
                    </div>
                </div>
            </div>

            {/* Detector Stats Section */}
            <h1 className='the-detec-h1-title' ref={triggerRef}>Instantaneous Trigger With Pinpoint Accuracy</h1>

            <div className="detec-stats-section">
                {Object.entries(detectorStats).map(([, value]) => (
                    <div key={value.name} className="stat-card">
                        <div className="stat-content">
                            <h3 className="stat-value">
                                {value.name === "Stroke Detection Efficiency" ? <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: "unset" }}><AnimatedCounter value={value.value} />%</p> : <AnimatedCounter value={value.value} />}
                            </h3>
                            <p
                                className="stat-label"
                                dangerouslySetInnerHTML={{ __html: value.name }}
                            ></p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Coverage Section */}
            <div className="detector-hero-section">
                <img className="hero-image" alt="hero background" src={detecBg3} />
                <div className="dark-overlay"></div> {/* ← Overlay */}
                <div className={`precision-info-wrapper align-left`}>
                    <div className='precision-infos'>
                        <h1><strong>Coverage That Drives Action</strong></h1>
                        <h2>
                            Our growing network empowers teams with real-time insights, supporting faster response and smarter
                            <span className='glow-blue'> fault mitigation</span> across regions.
                        </h2>
                        <button className='the-detector-button'
                            onClick={() => {
                                if (stratRef.current) {
                                    const yOffset = 0; // scroll 80px above the element
                                    const y = stratRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                    window.scrollTo({ top: y, behavior: 'smooth' });
                                }
                            }}>Learn More</button>
                    </div>
                </div>
            </div>

            {/* Network Stats Section */}
            <h1 className='the-detec-h1-title'>A Smarter Network for a Faster Response</h1>

            <div className="detec-stats-section">
                {Object.entries(NetStats).map(([, value]) => (
                    <div key={value.name} className="stat-card">
                        <div className="stat-content">
                            <h3 className="stat-value">
                                {value.name === "Utility faults accessed and analyzed" ? <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: "unset" }}><AnimatedCounter value={value.value} />K</p> : <AnimatedCounter value={value.value} />}
                            </h3>
                            <p
                                className="stat-label"
                                dangerouslySetInnerHTML={{ __html: splitLabel(value.name) }}
                            ></p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Strategy Section */}
            {!showMap && (
                <div className="detector-strat-section" ref={stratRef}>
                    <img className="hero-image2" alt="hero background" src={detecBg4} />
                    <div className="dark-overlay"></div> ← Overlay
                    <div className={`precision-info-wrapper align-right2`}>
                        <div className="precision-infos">
                            <h1><strong>Strategically Deployed. Ready When It Strikes.</strong></h1>
                            <h2>
                                Explore our footprint of High-Risk-Lightning™ sensors, enabling faster decisions and
                                <span className='glow-blue'> reduced response time</span>.
                            </h2>
                            <button
                                className='the-detec-button2'
                                onClick={() => {
                                    setShowMap(true);
                                    setTimeout(() => {
                                        mapRef.current?.scrollIntoView({ block: 'center' });
                                    }, 100); // Delay to wait for map to render
                                }}
                            >
                                <span>Explore Locations</span>
                            </button>

                        </div>
                    </div>
                </div>
            )}

            {/* Map Section */}
            {showMap && (
                <div className="detect-map" ref={mapRef}>

                    {hoveredFeature && (
                        <div className='states-info'>
                            <div className="states-hover-info">
                                <p>⚡ {hoveredFeature.name} ⚡</p>
                            </div>
                        </div>
                    )}

                    <div className='close-map'
                        onClick={() => {
                            setShowMap(false);
                            setTimeout(() => {
                                stratRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 100); // Delay to wait for map to render
                        }}>
                        Close Map
                    </div>


                    <MapContainer
                        center={[39.8283, -98.5795]} // Center of the contiguous US
                        zoom={4}
                        minZoom={3}
                        maxZoom={10}
                        maxBounds={[
                            [24.396308, -125.0], // Southwest corner (approximate southern California)
                            [49.384358, -66.93457] // Northeast corner (approximate Maine)
                        ]}
                        style={{ width: "100%", height: "100%" }}
                    >

                        {/* Add a TileLayer to display map tiles */}
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />

                        {/* Conditionally render GeoJSON layer when statesData is available */}
                        <GeoJSON
                            data={statesData as GeoJSON.GeoJsonObject}
                            style={computedStyle}
                            onEachFeature={onEachFeature} // Attach event handlers
                            ref={geojsonRef} // Assign reference to the GeoJSON layer
                        />

                    </MapContainer>
                </div>
            )}

            <SubscribeForm />

        </div>


    )
}

export default HRLDetector